import { useEffect, useMemo, useState } from 'react';
import { formatEther } from 'viem';
import { useAccount, useReadContract, useWaitForTransactionReceipt, useWriteContract } from 'wagmi';
import { RiCheckLine, RiCoinsLine, RiErrorWarningLine, RiLoader4Line } from '@remixicon/react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/sheet';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/card';
import { Badge } from '@/components/badge';
import { Button } from '@/components/button';
import { LARISSA_DAO_CONTRACT_CONFIG } from '@/configs/larissa-dao.config';
import type { NftDaoUtilsContractConfig } from '@/configs/nft.config';

type TxHash = `0x${string}`;

type RewardRow = {
  tokenId: bigint;
  level: bigint;
  rewardAmount: bigint;
  rewardAmountLRSN: bigint;
  isEligible: boolean;
  isFinalized: boolean;
};

function prettyClaimError(err: unknown) {
  const msg = err instanceof Error ? err.message : String(err ?? '');
  const lower = msg.toLowerCase();
  if (lower.includes('user rejected') || lower.includes('userrejected') || lower.includes('rejected')) {
    return 'Transaction rejected in wallet.';
  }
  // LarissaDaoV4 require codes:
  // "2" already claimed, "3" not finalized, "4" not eligible
  if (lower.includes('reverted') && (lower.includes('"2"') || lower.endsWith('2'))) return 'Already claimed.';
  if (lower.includes('reverted') && (lower.includes('"3"') || lower.endsWith('3'))) return 'Rewards not finalized yet.';
  if (lower.includes('reverted') && (lower.includes('"4"') || lower.endsWith('4'))) return 'NFT not eligible for this period.';
  return msg || 'Claim failed.';
}

export default function NftRewardClaimSidebar({
  isOpen,
  onClose,
  periodKey,
  utilsConfig,
  onClaimed,
}: {
  isOpen: boolean;
  onClose: () => void;
  periodKey: string | null;
  utilsConfig: NftDaoUtilsContractConfig;
  onClaimed?: () => void;
}) {
  const { address: userAccount } = useAccount();
  const [txHash, setTxHash] = useState<TxHash | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [claimingTokenId, setClaimingTokenId] = useState<bigint | null>(null);

  const { writeContractAsync, isPending: isWritePending } = useWriteContract();
  const { isLoading: isTxPending, isSuccess: isTxSuccess } = useWaitForTransactionReceipt({
    hash: txHash,
    query: { enabled: !!txHash },
  });

  const { data: rewards, isLoading: isLoadingRewards, refetch: refetchRewards } = useReadContract({
    address: utilsConfig.address,
    abi: utilsConfig.abi,
    functionName: 'getUserNftRewards',
    args: userAccount && periodKey ? [userAccount, periodKey] : undefined,
    query: { enabled: isOpen && !!userAccount && !!periodKey && !!utilsConfig.address },
  }) as { data: RewardRow[] | undefined; isLoading: boolean; refetch: () => void };

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setTxHash(undefined);
    setClaimingTokenId(null);
  }, [isOpen, periodKey]);

  useEffect(() => {
    if (!isTxSuccess) return;
    setTxHash(undefined);
    setClaimingTokenId(null);
    setError(null);
    onClaimed?.();
    void refetchRewards();
  }, [isTxSuccess, onClaimed, refetchRewards]);

  const claimableRows = useMemo(() => {
    if (!rewards) return [];
    // We don’t have a batch claim-status read in app ABI, so we show claim buttons
    // and rely on contract revert ("2") + post-claim refresh.
    return rewards.filter((r) => r.isFinalized && r.isEligible && (r.rewardAmount > 0n || r.rewardAmountLRSN > 0n));
  }, [rewards]);

  const totals = useMemo(() => {
    if (!claimableRows.length) return null;
    const totalLrs = claimableRows.reduce((acc, r) => acc + r.rewardAmount, 0n);
    const totalLrsn = claimableRows.reduce((acc, r) => acc + r.rewardAmountLRSN, 0n);
    return { totalLrs, totalLrsn };
  }, [claimableRows]);

  const isBusy = isWritePending || isTxPending;

  const handleClaim = async (tokenId: bigint) => {
    if (!periodKey || !LARISSA_DAO_CONTRACT_CONFIG.address) return;
    try {
      setError(null);
      setClaimingTokenId(tokenId);
      const hash = await writeContractAsync({
        address: LARISSA_DAO_CONTRACT_CONFIG.address,
        abi: LARISSA_DAO_CONTRACT_CONFIG.abi,
        functionName: 'claimNftMemberReward',
        args: [tokenId, periodKey],
      });
      setTxHash(hash as TxHash);
    } catch (e) {
      setError(prettyClaimError(e));
      setClaimingTokenId(null);
    }
  };

  return (
    <Sheet
      open={isOpen}
      onOpenChange={() => {
        onClose();
      }}
    >
      <SheetContent side="right" className="sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-primary">Claim rewards</SheetTitle>
          <SheetDescription>
            {periodKey ? (
              <>
                Claim any finalized rewards for <span className="font-medium text-foreground/80">{periodKey}</span>.
              </>
            ) : (
              'Select a reward period to claim.'
            )}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 mt-6">
          {!userAccount && (
            <div className="rounded-md border border-border bg-sidebar/60 px-3 py-2 text-sm text-muted-foreground">
              Connect your wallet to view claimable rewards.
            </div>
          )}

          {totals && (
            <Card className="bg-sidebar border border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <RiCoinsLine className="size-5 text-primary" aria-hidden />
                  Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Total (LRS)</span>
                  <span className="font-medium tabular-nums">{parseFloat(formatEther(totals.totalLrs)).toFixed(6)} LRS</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Total (LRSN)</span>
                  <span className="font-medium tabular-nums">{parseFloat(formatEther(totals.totalLrsn)).toFixed(6)} LRSN</span>
                </div>
              </CardContent>
            </Card>
          )}

          {error && (
            <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200 flex gap-2">
              <RiErrorWarningLine className="size-4 mt-0.5 shrink-0" aria-hidden />
              <span className="min-w-0 break-words">{error}</span>
            </div>
          )}

          <Card className="bg-sidebar border border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Eligible NFTs</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {isLoadingRewards ? (
                <div className="flex items-center justify-center py-8">
                  <RiLoader4Line className="size-6 animate-spin text-primary" aria-hidden />
                </div>
              ) : !periodKey ? (
                <div className="text-sm text-muted-foreground">Pick a month to claim.</div>
              ) : !rewards || rewards.length === 0 ? (
                <div className="text-sm text-muted-foreground">No NFTs found for this wallet.</div>
              ) : claimableRows.length === 0 ? (
                <div className="text-sm text-muted-foreground">No claimable rewards for this period.</div>
              ) : (
                <div className="space-y-2">
                  {claimableRows.map((r) => {
                    const isClaiming = claimingTokenId === r.tokenId && isBusy;
                    return (
                      <div
                        key={r.tokenId.toString()}
                        className="rounded-lg border border-border bg-background/50 px-3 py-2.5 flex items-center justify-between gap-3"
                      >
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">#{r.tokenId.toString()}</span>
                            <Badge variant="outline" className="text-xs">
                              L{r.level.toString()}
                            </Badge>
                          </div>
                          <div className="text-xs text-muted-foreground tabular-nums">
                            {parseFloat(formatEther(r.rewardAmount)).toFixed(6)} LRS ·{' '}
                            {parseFloat(formatEther(r.rewardAmountLRSN)).toFixed(6)} LRSN
                          </div>
                        </div>

                        <Button
                          size="sm"
                          disabled={!userAccount || isBusy}
                          onClick={() => void handleClaim(r.tokenId)}
                          className="cursor-pointer bg-primary hover:bg-primary/90 text-primary-foreground"
                        >
                          {isClaiming ? (
                            <>
                              <RiLoader4Line className="size-4 animate-spin mr-2" aria-hidden />
                              Claiming…
                            </>
                          ) : (
                            <>
                              <RiCheckLine className="size-4 mr-1" aria-hidden />
                              Claim
                            </>
                          )}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </SheetContent>
    </Sheet>
  );
}

